import { NgxsDataPluginModule } from '@angular-ru/ngxs';
import { NGXS_DATA_STORAGE_PLUGIN } from '@angular-ru/ngxs/storage';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { MsalModule } from '@azure/msal-angular';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { provideNgxWebstorage, withLocalStorage, withSessionStorage } from 'ngx-webstorage';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TelephonyQueryParamsService } from 'src/app/services/telephony/telephony-queryparams.service';
import { environment } from 'src/environments/environment';
import { AppErrorHandlerInterceptor } from './app-error-handler.interceptor';
import { AppErrorHandler } from './app-error.handler';
import { AppRoleHeaderInterceptor } from './app-role-handler.interceptor';
import { lazyArrayToObj } from './app-routing-lazy';
import { AppMainRoutingModule } from './app-routing.module';
import { CustomUrlSerializer } from './app-url.handler';
import { AppComponent } from './app.component';
import { RightPanelService } from './components/rightpanel/rightpanel.service';
import { DocumentTypesManagerModule } from './pages/document-types/document-types.module';
import { TenantModule } from './pages/tenant/tenant.module';
import { CommunityProgramsService } from './services/+products/community/community-programs.service';
import { LISService } from './services/+products/lis/lis.service';
import { MedicaidService } from './services/+products/medicaid/medicaid.service';
import { ProductOrchestratorService } from './services/+products/product-orchestrator.service';
import { SNAPService } from './services/+products/snap/snap.service';
import { SSIService } from './services/+products/ssi/ssi.service';
import { VeteranService } from './services/+products/veteran/veteran.service';
import { StateAggregator } from './services/+redux/state-aggregator.service';
import { AlertService } from './services/alert.service';
import {
    AppInitializerService,
    BASE_URL_TOKEN,
    baseUrl,
    initializerFactory,
} from './services/app-initializer/app-initializer.service';
import { AppInsightsService } from './services/app-insights/appinsights.service';
import { ConfigurationService } from './services/configuration/configuration.service';
import { DocumentService } from './services/document.service';
import { FacilityRequestService } from './services/facility-request/facility-request.service';
import { serviceFactoryInjectToken } from './services/global/service-factory.models';
import { ServiceFactoryService } from './services/global/service-factory.service';
import { InterviewProvidersModule } from './services/interview-providers/interview-providers.module';
import { LayoutService } from './services/layout.service';
import { MemberListState } from './services/member/member-list.state.service';
import { MemberService } from './services/member/member.service';
import { ViewRepositoryService } from './services/process-manager/view-repository.service';
import { CommandLazyDataService } from './services/task/command/command-lazy-data.service';
import { CommandService } from './services/task/command/command.service';
import { SystemEvaluatorService } from './services/task/system-evaluator/system-evaluator.service';
import { TaskServiceFactory, taskServiceFactory } from './services/task/task.service';
import { TenantService } from './services/tenant/tenant.service';
import { RoleService } from './services/user/role.service';
import { UserPermissionService } from './services/user/user-permission.service';
import { UserService } from './services/user/user.service';
import { DataContextBucketRepositoryService } from './shared/data-context-repository/data-context-repository.service';
import { ErrorModule } from './shared/error/error.module';
import { LayoutsModule } from './shared/layouts/layouts.module';
import { LAZY_WIDGETS } from './shared/lazy-loader/tokens';
import { NavigationHandlerService } from './shared/navigation-handler/navigation-handler.service';
import { FeatureService } from './shared/preview-feature/preview-feature.service';
import { SecurityProviders } from './shared/security/security-module';
import { AppSharedModule } from './shared/shared.module';
import { ActionHandlerDIProviders } from './components/smart-icon/app-actions-di.mapping';
import { MemberHistoryService } from './services/member-history.service';
import { NotificationService } from './services/notification/notification.service';
import { BlazorInteropService } from './components/blazor-shell/blazor-interop.service';

@NgModule({
    declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        AppSharedModule,
        AppMainRoutingModule,
        MsalModule,
        BrowserModule,
        BrowserAnimationsModule,
        ErrorModule,
        LayoutsModule,
        InterviewProvidersModule,
        TenantModule,
        NgxsModule.forRoot([
            ConfigurationService,
            UserService,
            RoleService,
            UserPermissionService,
            LayoutService,
            RightPanelService,
            TenantService,
            MemberService,
            MemberListState,
            StateAggregator,
            ProductOrchestratorService,
            FacilityRequestService,
            CommunityProgramsService,
            MedicaidService,
            LISService,
            SNAPService,
            VeteranService,
            SSIService,
            FeatureService,
            TelephonyQueryParamsService,
            NavigationHandlerService,
            DocumentService,
            DataContextBucketRepositoryService,
            ViewRepositoryService,
            MemberHistoryService,
            NotificationService
        ], {
            developmentMode: !environment.production
        }),
        NgxsDataPluginModule.forRoot(),
        NgxsDataPluginModule.forRoot([NGXS_DATA_STORAGE_PLUGIN]),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
        GoogleMapsModule,
        DocumentTypesManagerModule
    ],
    providers: [
        provideHttpClient(),
        { provide: BASE_URL_TOKEN, useValue: baseUrl },
        { provide: APP_INITIALIZER, useFactory: initializerFactory, deps: [AppInitializerService, BASE_URL_TOKEN], multi: true },
        { provide: ErrorHandler, useClass: AppErrorHandler },
        { provide: UrlSerializer, useClass: CustomUrlSerializer },
        { provide: LAZY_WIDGETS, useFactory: lazyArrayToObj },
        MessageService,
        ConfirmationService,
        DatePipe,
        AppInsightsService,
        BlazorInteropService,
        provideNgxWebstorage(
            withLocalStorage(),
            withSessionStorage()
        ),

        ...SecurityProviders,

        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppRoleHeaderInterceptor,
            deps: [
                Store
            ],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppErrorHandlerInterceptor,
            deps: [
                AppInsightsService,
                ActivatedRoute,
                Store,
                Injector
            ],
            multi: true
        },
        {
            provide: TaskServiceFactory,
            useFactory: taskServiceFactory,
            deps: [
                SystemEvaluatorService,
                CommandService,
                FeatureService,
                AlertService,
                Router,
                CommandLazyDataService
            ]
        },
        {
            provide: serviceFactoryInjectToken,
            useClass: ServiceFactoryService
        },
        provideHttpClient(withInterceptorsFromDi()),

        ...ActionHandlerDIProviders

    ],
})
export class AppModule {
}
